import React from 'react';
import { MapContainer, TileLayer, Marker, Popup, Polyline } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import dayjs from 'dayjs';

// Custom icon configuration
const configureLeafletIcons = () => {
  delete L.Icon.Default.prototype._getIconUrl;
  
  // Default footprint icon for route points
  L.Marker.prototype.options.icon = L.icon({
    iconUrl: '/images/leaflet/footprint.png',
    iconSize: [20, 20],
    iconAnchor: [10, 20],
    popupAnchor: [0, -20]
  });
};

// Create custom icons
const createCustomIcon = (iconUrl, size = [25, 41]) => L.icon({
  iconUrl,
  iconSize: size,
  iconAnchor: [size[0]/2, size[1]],
  popupAnchor: [0, -size[1]]
});

configureLeafletIcons();

const LeafletMapping = ({ data, vehicleName }) => {
  try {
    // Process coordinate data
    const coordinates = data
      ?.filter(item => item?.Lat && item?.Lon && item?.GT)
      ?.map(item => ({
        lat: parseFloat(item.Lat),
        lng: parseFloat(item.Lon),
        time: item.GT,
        speed: item.Speed || 0,
        fv1: item.FV1 || 'N/A',
        accuracy: item.ACC || 'N/A'
      })) || [];

    // Generate path color
    const pathColor = '#3B82F6'; // Consistent blue for the path

    // Calculate bounds
    const bounds = coordinates.length > 0 
      ? L.latLngBounds(coordinates.map(coord => [coord.lat, coord.lng]))
      : null;

    if (!bounds || coordinates.length === 0) {
      return (
        <div className="flex items-center justify-center h-64 bg-gray-50 rounded-lg">
          <p className="text-gray-500">No valid location data available</p>
        </div>
      );
    }

    // Get first and last points
    const startPoint = coordinates[0];
    const endPoint = coordinates[coordinates.length - 1];
    const routePoints = coordinates.slice(1, -1);

    return (
      <div className="h-[500px] w-full rounded-lg overflow-hidden shadow-md">
        <MapContainer 
          bounds={bounds}
          style={{ height: '100%', width: '100%' }}
          zoom={13}
          scrollWheelZoom={true}
          className="z-0"
        >
          <TileLayer
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
          />
          
          {/* Vehicle path */}
          <Polyline
            positions={coordinates.map(coord => [coord.lat, coord.lng])}
            color={pathColor}
            weight={5}
            opacity={0.7}
          />
          
          {/* Start Point (Green) */}
          {startPoint && (
            <Marker 
              position={[startPoint.lat, startPoint.lng]}
              icon={createCustomIcon('/images/leaflet/start-marker.png', [45, 45])}
            >
              <Popup>
                <div className="space-y-1">
                  <h4 className="font-bold">START: {vehicleName}</h4>
                  <p>{dayjs(startPoint.time).format('YYYY-MM-DD HH:mm:ss')}</p>
                </div>
              </Popup>
            </Marker>
          )}

          {/* Route Points (Footprints) */}
          {routePoints.map((coord, index) => (
            <Marker 
              key={`route-${index}`}
              position={[coord.lat, coord.lng]}
              opacity={0.7} // Make footprints slightly transparent
            >
              <Popup>
                <div className="space-y-1">
                  <p><strong>Route Point {index + 1}</strong></p>
                  <p>{dayjs(coord.time).format('HH:mm:ss')}</p>
                  <p>Speed: {coord.speed} km/h</p>
                </div>
              </Popup>
            </Marker>
          ))}

          {/* End Point (Red) */}
          {endPoint && (
            <Marker 
              position={[endPoint.lat, endPoint.lng]}
              icon={createCustomIcon('/images/leaflet/end-marker.png', [45, 45])}
            >
              <Popup>
                <div className="space-y-1">
                  <h4 className="font-bold">END: {vehicleName}</h4>
                  <p>{dayjs(endPoint.time).format('YYYY-MM-DD HH:mm:ss')}</p>
                  <p>Total Points: {coordinates.length}</p>
                </div>
              </Popup>
            </Marker>
          )}
        </MapContainer>
      </div>
    );
  } catch (error) {
    console.error('Error in LeafletMapping:', error);
    return (
      <div className="flex items-center justify-center h-64 bg-red-50 rounded-lg">
        <p className="text-red-500">Error displaying map data</p>
      </div>
    );
  }
};

export default LeafletMapping;


